<template>
  <div>
    <!-- payment notification -->
    <div
      v-show="shouldRenderNotification"
      id="global-notification-bar"
      :class="notificationClasses"
      @click="$router.push('/payments')"
    >
      {{ $t("notifications.trailEndIn", { days: timeLeftForTrial }) }}
    </div>

    <!-- top bar -->
    <div class="flex bg-white p-2 items-center">
      <!-- Menu icon - Mobile Only -->
      <div class="block mr-4 ml-2 md:hidden">
        <font-awesome-icon icon="bars" @click="onMenuClick" />
        <r-menu
          v-if="isMenuVisible"
          @on-menu-press="onMenuClick"
          class="divide-y divide-gray-400"
        >
          <div
            class="flex flex-row ml-3 pt-4"
            v-for="(item, i) in menuItems"
            :key="i"
          >
            <div
              class="w-full flex flex-row mb-4"
              @click="onItemPress(item.to)"
            >
              <r-icon :icon="item.icon" color="primary" />
              <!-- <font-awesome-icon :icon="item.icon" size="lg" class="mr-3" /> -->
              <div class="ml-2 mt-2 flex">
                {{ $t(`${item.key}.table.title`) }}
              </div>
            </div>
          </div>
        </r-menu>
      </div>

      <!-- logo -->
      <img
        src="@/assets/icons/logo-small.svg"
        class="h-8 mr-6 md:ml-3 cursor-pointer"
        @click="$router.push('/')"
      />

      <r-button
        color="success"
        class="flex items-center"
        @click="openWhatsappLink"
      >
        <!-- whatsapp icon -->
        <img src="@/assets/icons/whatsapp.png" class="w-6 mr-2" />

        <!-- text -->
        {{ $t("globals.contactClara") }}
      </r-button>

      <div class="flex-auto" />

      <div class="static">
        <div class="flex flex-row mr-1">
          <!-- user name -->
          <div class="hidden sm:flex mr-3 mt-2">
            {{ $t("globals.hi") }}
            {{ displayName }}
          </div>

          <!-- user avatar -->
          <div @click="toggleDropdown" class="cursor-pointer">
            <img
              v-if="(user || {}).photoURL"
              :src="(user || {}).photoURL"
              class="w-8 rounded-full bg-r-primary"
            />
            <div
              v-else
              class="h-10 w-10 rounded-full bg-purple-600 text-white flex items-center justify-center"
            >
              <span class="bg-grey-600">
                {{ userShortcut }}
              </span>
            </div>
          </div>
        </div>

        <!-- dropdown -->
        <r-dropdown v-model="isDropOpen">
          <!-- Bot settings -->
          <div
            class="flex flex-row px-4 py-2 text-gray-800 hover:bg-r-primary hover:text-white cursor-pointer"
            @click="openSettingsDialog"
          >
            <div class="w-8">
              <font-awesome-icon icon="user-cog" class="mr-3" />
            </div>
            <div>{{ $t("buttons.botSettings") }}</div>
          </div>

          <!-- Console settings -->
          <div
            class="flex flex-row px-4 py-2 text-gray-800 hover:bg-r-primary hover:text-white cursor-pointer"
            @click="openConsoleSettings"
          >
            <div class="w-8">
              <font-awesome-icon icon="globe" class="mr-3" />
            </div>
            <div>{{ $t("buttons.consoleSettings") }}</div>
          </div>

          <!-- Delete Company -->
          <div
            class="flex flex-row px-4 py-2 text-gray-800 hover:bg-r-primary hover:text-white cursor-pointer"
            @click="toggleDeleteCompany"
          >
            <div class="w-8">
              <font-awesome-icon icon="trash-alt" class="mr-3" />
            </div>
            <div>{{ $t("buttons.deleteCompany") }}</div>
          </div>

          <!-- Sign out -->
          <div
            class="flex flex-row px-4 py-2 text-gray-800 hover:bg-r-primary hover:text-white cursor-pointer"
            @click="signOut"
          >
            <div class="w-8">
              <font-awesome-icon icon="sign-out-alt" class="mr-3" />
            </div>
            <div>{{ $t("buttons.signOut") }}</div>
          </div>
        </r-dropdown>
      </div>
    </div>

    <!-- side bar -->
    <div class="invisible md:visible">
      <r-side-bar>
        <!-- menu -->
        <router-link
          v-for="menuItem of menuItems"
          :key="menuItem.key"
          :to="menuItem.to"
        >
          <r-icon :icon="menuItem.icon" />
        </router-link>
      </r-side-bar>
    </div>

    <slot></slot>

    <!-- Settings dialog -->
    <r-dialog id="settings-dialog" v-model="settingsDialog.open">
      <div class="flex flex-col bg-white h-full w-full rounded shadow-lg p-4">
        <!-- title -->
        <div class="font-bold">
          {{ $t("botSettings.title") }}
        </div>

        <!-- Select bot lang -->
        <r-select
          id="number-select"
          v-model="settingsDialog.claraNumber"
          class="w-full mt-5 mb-2 w-48"
          :label="$t('forms.claraNumber')"
          :options="claraNumberList"
        />

        <r-select
          id="bot-lang-select"
          v-model="settingsDialog.botLang"
          class="w-full mt-3 mb-8 w-48"
          :label="$t('forms.botLang')"
          :options="botLangSettings"
        />
        <!-- buttons -->
        <div class="flex flex-row w-full justify-end">
          <!-- cancel -->
          <r-button
            id="settings-close-dialog"
            class="mr-4"
            text
            color="black"
            @click="closeSettingsDialog"
          >
            {{ $t("buttons.cancel") }}
          </r-button>

          <!-- done -->
          <r-button
            id="settings-save-dialog"
            @click="saveSettings"
            :loading="settingsDialog.loading"
          >
            {{ $t("buttons.save") }}
          </r-button>
        </div>
      </div>
    </r-dialog>

    <!-- Console dialog -->
    <r-dialog id="console-dialog" v-model="consoleDialog.open">
      <div class="flex flex-col bg-white h-full w-full rounded shadow-lg p-4">
        <!-- title -->
        <div class="font-bold">
          {{ $t("consoleSettings.title") }}
        </div>

        <!-- Select bot lang -->
        <r-select
          id="console-locale-select"
          v-model="consoleDialog.locale"
          class="w-full mt-5 mb-5 w-48"
          :label="$t('consoleSettings.locale')"
          :options="botLangSettings"
        />

        <!-- buttons -->
        <div class="flex flex-row w-full justify-end">
          <!-- cancel -->
          <r-button
            id="console-close-dialog"
            class="mr-4"
            text
            color="black"
            @click="closeConsoleDialog"
          >
            {{ $t("buttons.cancel") }}
          </r-button>

          <!-- done -->
          <r-button
            id="console-save-dialog"
            @click="saveConsole"
            :loading="consoleDialog.loading"
          >
            {{ $t("buttons.save") }}
          </r-button>
        </div>
      </div>
    </r-dialog>

    <!-- Delete company dialog -->
    <r-dialog id="delete-company-dialog" v-model="deleteCompanyDialog.open">
      <div class="flex flex-col bg-white h-full w-full rounded shadow-lg p-4">
        <!-- title -->
        <div class="font-bold">
          {{ $t("deleteCompany.title") }}
        </div>

        <!-- description -->
        <p v-html="$t('deleteCompany.description')" class="mb-6 mt-6" />

        <!-- buttons -->
        <div class="flex flex-row w-full justify-end">
          <!-- cancel button -->
          <r-button
            id="console-close-dialog"
            class="mr-4"
            text
            color="black"
            @click="toggleDeleteCompany"
          >
            {{ $t("buttons.cancel") }}
          </r-button>

          <!-- delete button -->
          <r-button
            id="console-save-dialog"
            color="error"
            @click="submitDeleteCompany"
            :loading="deleteCompanyDialog.loading"
          >
            {{ $t("buttons.delete") }}
          </r-button>
        </div>
      </div>
    </r-dialog>
  </div>
</template>

<script>
import componentAPI from "../api/components"
import moment from "moment"
import { mapGetters } from "vuex"
import firebase from "firebase/app"

/**
 * Global settings of the layout
 */
export default {
  // local state
  data() {
    return {
      // dropdown
      isDropOpen: false,

      // bot settings dialog
      settingsDialog: {
        open: false,
        loading: false,
        botLang: "",
        claraNumber: ""
      },

      // console settings dialog
      consoleDialog: {
        open: false,
        loading: false,
        locale: ""
      },

      // delete company dialog
      deleteCompanyDialog: {
        open: false,
        loading: false
      },

      // menu
      isMenuVisible: false,

      // menu items
      menuItems: [
        {
          key: "usersManagement",
          icon: "user-circle",
          to: "/"
        },
        {
          key: "integrations",
          icon: "setting",
          to: "/integrations"
        },
        {
          key: "payments",
          icon: "bill",
          to: "/payments"
        }
      ]
    }
  },

  // calculated props
  computed: {
    ...mapGetters({
      // auth attributes
      user: "auth/getUser",
      // displayName: "auth/getDisplayName",
      userDoc: "users/getDoc",

      // org attributes
      org: "orgs/getDoc",

      // pull oauth grant access and check if should redirect
      oauthGrantRequestParams: "auth/getOauthGrantRequestParams"
    }),

    /**
     * Returns the display name
     */
    displayName() {
      const storeDisplayName = this.$store.getters["auth/getDisplayName"]

      if (storeDisplayName) {
        return storeDisplayName
      } else if (this.userDoc.first_name && this.userDoc.last_name) {
        return `${this.userDoc.first_name} ${this.userDoc.last_name}`
      } else {
        return " "
      }
    },

    /**
     * Returns Clara's bot number.
     */
    claraNumber() {
      // const globalClaraNumber = "41772016402" // swiss international number
      return this?.org?.clara_number || "573195037823"
    },

    // clara number list
    claraNumberList() {
      if (process.env.VUE_APP_FIRE_ENV === "production") {
        return [
          { display: "International", value: "41772022053" },
          { display: "Colombia", value: "573195037823" },
          { display: "Mexico", value: "525544622329" }
        ]
      } else {
        return [{ display: "International", value: "41772016402" }]
      }
    },

    // returns bot settings
    botLangSettings() {
      return [
        {
          value: "EN",
          display: this.$t("botOptions.en")
        },
        {
          value: "ES",
          display: this.$t("botOptions.es")
        },
        {
          value: "FR",
          display: this.$t("botOptions.fr")
        },
        {
          value: "GR",
          display: this.$t("botOptions.gr")
        },
        {
          value: "PT",
          display: this.$t("botOptions.pt")
        }
      ]
    },

    /**
     * Notification classes
     */
    notificationClasses() {
      return {
        "bg-r-primary": this.daysToEndTrial > 7,
        "bg-red-600": this.daysToEndTrial <= 7,
        "p-2": true,
        "text-white": true,
        "font-semibold": true,
        "cursor-pointer": true,
        "text-center": true
      }
    },

    /**
     * Should render notification about
     * payments
     */
    shouldRenderNotification() {
      return (
        this.userDoc &&
        this.timeLeftForTrial &&
        !this.org.subscription &&
        !this.$route.path.startsWith("/auth")
      )
    },

    /**
     * calc Days between
     */
    daysToEndTrial() {
      if (this.org && this.org.create_timestamp) {
        const trialEndDate = moment(this.org.create_timestamp.toDate()).add(
          this.org.trail_days,
          "days"
        )
        return trialEndDate.diff(moment(), "days")
      }

      return 30
    },

    /**
     * Returns the notification height
     */
    notificationHeight() {
      if (!process.server && document) {
        const elem = document.getElementById("global-notification-bar")
        if (elem) {
          return elem.clientHeight - 15 + "px"
        }
      }

      return "0px"
    },

    /**
     * Calculate days left for trial
     */
    timeLeftForTrial() {
      if (this.org && this.org.create_timestamp) {
        const createDate = moment(this.org.create_timestamp.toDate())
        createDate.add(this.org.trail_days, "days")
        return moment(createDate).fromNow()
      }

      return ""
    },

    /**
     * Determine if trial is over
     */
    isTrialOver() {
      if (this.org && this.org.create_timestamp) {
        const createDate = moment(this.org.create_timestamp.toDate())
        createDate.add(this.org.trail_days, "days")
        return moment().isAfter(createDate)
      }

      return false
    },

    /**
     * Get uset shortcut
     */
    userShortcut() {
      const [firstName, lastName] = (this.displayName || " ").split(" ")
      return (firstName || "").charAt(0) + (lastName || "").charAt(0)
    },

    /**
     * Determine if redirect to payment page needed
     */
    isRedirectToPayments() {
      return (
        this.isTrialOver &&
        this.$route.path !== "/payments" &&
        !(this.org || {}).subscription
      )
    }
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  // watch properties
  watch: {
    // monitor the user access to bind data
    userDoc: {
      immediate: true,
      handler() {
        console.log("userDoc handler: ", this.userDoc)
        if ((((this.userDoc || {}).orgs || [])[0] || {}).length) {
          this.bindOrgData()
        }
      }
    },

    // org watcher
    org: {
      immediate: true,
      handler() {
        // redirect to payments if trial over
        if (this.isRedirectToPayments) {
          this.$router.push("/payments")
        }

        const botLang = this.org?.bot_lang || "EN"
        const claraNumber =
          this.org?.clara_number || this.claraNumberList[0].value
        this.$set(this.settingsDialog, "botLang", botLang)
        this.$set(this.settingsDialog, "claraNumber", claraNumber)
      }
    }
  },

  /**
   * Updated hook
   */
  async beforeMount() {
    await this.checkRedirects()
  },

  /**
   * Bind on load
   */
  mounted() {
    console.log("user doc mounted: ", this.userDoc)
    this.checkOauthGrantRedirect()

    if ((((this.userDoc || {}).orgs || [])[0] || {}).length) {
      this.bindOrgData()
    }
  },

  // component functions
  methods: {
    async checkRedirects() {
      console.log("checking redirects in default")
      const currentUser = firebase.auth().currentUser
      const userFsId = this.$store.getters["auth/getUserFsId"]

      // if user is authenticated:
      // start check if redirect needed
      if (currentUser) {
        const jwt = await currentUser.getIdToken()

        // if userFsId Reltaed to user
        if (userFsId) {
          // get user
          const { user = {} } = await componentAPI.users(jwt).get(userFsId)

          // Step 2 - check if org releted.
          if (this.isRedirectToGenOrg(user)) {
            this.$router.push("/auth/gen-org")
          } else if (await this.isRedirectToFirstIntegration(user, jwt)) {
            // Step 3 - check if there is one inregration related
            this.$router.push("/auth/first-integration")
          }
          // Step 1 - check if userFsId is related
        } else if (this.isRedirectToNewUser(userFsId)) {
          this.$router.push("/auth/new-user")
        }
      }

      if (this.isRedirectToPayments) {
        this.$router.push("/payments")
      }
    },

    /**
     * Check oauth grant access redirect params
     */
    checkOauthGrantRedirect() {
      console.log("params oauth grant request", this.oauthGrantRequestParams)

      // if params exists
      if (Object.keys(this.oauthGrantRequestParams).length) {
        this.$router.push("/integrations/oauth-grant-access")
      }
    },

    /**
     * Save console dialog
     */
    async saveConsole() {
      this.$set(this.consoleDialog, "loading", true)
      const consoleLang = this.consoleDialog.locale.toLowerCase()

      try {
        // update console lang on firestore
        await this.$store.dispatch("users/update", {
          id: this.userDoc?.id,
          data: {
            consoleLang: consoleLang.toUpperCase()
          }
        })

        // update console lang in local storage
        localStorage.setItem("console-locale", consoleLang || "en")
      } catch (e) {
        console.error(e)
      }

      this.closeConsoleDialog()
      location.reload()
    },

    /**
     * open console settings
     */
    openConsoleSettings() {
      const currentLocale =
        this.userDoc?.console_lang ||
        localStorage.getItem("console-locale") ||
        "en"
      this.$set(this.consoleDialog, "locale", currentLocale.toUpperCase())
      this.$set(this.consoleDialog, "open", true)
    },

    /**
     * Close console dialog
     */
    closeConsoleDialog() {
      this.$set(this.consoleDialog, "open", false)
      this.$set(this.consoleDialog, "locale", "")
      this.$set(this.consoleDialog, "loading", false)
    },

    /**
     * Determine if is Redirect To First Integration
     */
    async isRedirectToFirstIntegration(user, jwt) {
      const whitelist = [
        "/auth/first-integration",
        "/integrations/oauth-grant-access"
      ]

      if (!whitelist.includes(this.$route.path)) {
        const { org = {} } = await componentAPI.orgs(jwt).get(user.orgsList[0])
        if (Object.keys(org.hubspot || {}).length) return false
        else if (Object.keys(org.zapier || {}).length) return false
        else if (((user || {}).hubspotMap || []).length) return false
        else if (((user || {}).mailingMap || []).length) return false
        else if (((user || {}).salesforceMap || []).length) return false
        else if (((user || {}).zapierMap || []).length) return false
        else return true
      } else {
        return false
      }
    },

    /**
     * Determine if redirect to gen org
     */
    isRedirectToGenOrg(user) {
      return (
        this.$route.path !== "/auth/gen-org" &&
        !((user || {}).orgsList || []).length
      )
    },

    /**
     * Determine if redirect to new user
     */
    isRedirectToNewUser(userFsId) {
      return this.$route.path.path !== "/auth/new-user" && !userFsId
    },

    /**
     * On menu item press
     */
    onItemPress(path) {
      this.$router.push(path)
      this.isMenuVisible = false
    },

    /**
     * On menu click
     */
    onMenuClick() {
      this.isMenuVisible = !this.isMenuVisible
    },

    /**
     * Save settings
     */
    async saveSettings() {
      try {
        const botLangs = {
          en: 1,
          es: 2,
          fr: 3,
          gr: 4,
          pt: 6
        }

        this.$set(this.settingsDialog, "loading", true)
        await this.$store.dispatch("orgs/update", {
          id: this.org?.id,
          data: {
            claraNumber: this.settingsDialog.claraNumber,
            botLang: botLangs[this.settingsDialog.botLang.toLowerCase()]
          }
        })

        this.$set(this.settingsDialog, "loading", false)
        this.closeSettingsDialog()
      } catch (e) {
        console.error(e)
        this.notifyError({
          title: this.$t("errors.unableToSaveBotSettings"),
          message: e.message
        })
      }
    },

    /**
     * Close settings dialog
     */
    closeSettingsDialog() {
      this.$set(this.settingsDialog, "open", false)
    },
    /**
     * Bind organization data
     */
    bindOrgData() {
      this.$store.dispatch("orgs/bindDoc", this.userDoc.orgs[0])
    },

    /**
     * Open whatsapp link
     */
    openWhatsappLink() {
      const msg = "/start"
      const win = window.open(
        `https://api.whatsapp.com/send?phone=${this.claraNumber}&text=${msg}`,
        "_blank"
      )
      win.focus()
    },

    /**
     * Sign out from firestore session
     */
    signOut() {
      firebase
        .auth()
        .signOut()
        .then(() => {
          this.$store.dispatch("reset")
          this.$router.replace("/auth")
        })
    },

    /**
     * On avatar press
     */
    toggleDropdown() {
      this.isDropOpen = !this.isDropOpen
    },

    /**
     * open Settings Dialog
     */
    openSettingsDialog() {
      this.$set(this.settingsDialog, "open", true)
    },

    /**
     * toggle Delete company dialog
     */
    toggleDeleteCompany() {
      this.$set(
        this.deleteCompanyDialog,
        "open",
        !this.deleteCompanyDialog.open
      )
    },

    /**
     * Submit delete company
     */
    async submitDeleteCompany() {
      // toggel loader
      this.$set(this.deleteCompanyDialog, "loading", true)
      try {
        // pull jwt
        const jwt = await firebase.auth().currentUser.getIdToken()

        // delete all user's related the org
        await componentAPI.orgs(jwt).deleteCompany(this.org.id)

        // reload page after deletion
        location.reload()
      } catch (e) {
        // toggel loader
        this.$set(this.deleteCompanyDialog, "loading", false)
        this.notifyError({
          title: this.$t("errors.unableToDeleteCompany"),
          message: e.message || "unable to delete company."
        })
      }
    }
  }
}
</script>
